import { defineStore } from 'pinia';

import Logger from '@evidentid/logger';
import Tracker from '@evidentid/tracker';

import { LocalStorageKeys } from '@/modules/local-storage/types';

export interface LocalStorageRequirement {
    tracker?: Tracker<any>;
    logger?: Logger<any>;
}

export interface UserPreference {
    sidenavOpened: boolean;
}

interface LocalStorageState {
    userPreferences: Record<string, UserPreference>;
}

// The legacy module fired these through app.observer.onAction() hooks in its
// module-builder registration; with $app available inside actions the
// tracking is inlined instead. Names/messages kept verbatim.
function trackPreferenceEvent(app: LocalStorageRequirement, name: string, message: string, email: string): void {
    app.tracker?.track({ name, description: message, email });
    app.logger?.addBreadcrumb({
        category: 'Local Storage - User Preference',
        message,
        data: { email },
    });
}

export const useLocalStorageStore = defineStore('localStorage', {
    state: (): LocalStorageState => ({
        userPreferences: {},
    }),
    actions: {
        loadUserPreference(payload: { email: string }) {
            const { email } = payload;
            trackPreferenceEvent(
                this.$app as LocalStorageRequirement,
                'Load User Preference from Local Storage',
                `[Requested] Load User Preference for ${email}`,
                email,
            );
            const preferencesString = window.localStorage.getItem(LocalStorageKeys.userPreferences);
            const preferences = preferencesString ? JSON.parse(preferencesString) : null;
            const preference = email && preferences ? preferences[email] : {};
            this.userPreferences = {
                ...this.userPreferences,
                [email]: { ...preference },
            };
        },
        saveUserPreference(payload: { email: string, preference: UserPreference }) {
            const { email, preference } = payload;
            trackPreferenceEvent(
                this.$app as LocalStorageRequirement,
                'Save User Preference to Local Storage',
                `[Requested] Save User Preference for ${email}`,
                email,
            );
            this.userPreferences = {
                ...this.userPreferences,
                [email]: { ...preference },
            };
            window.localStorage.setItem(LocalStorageKeys.userPreferences, JSON.stringify(this.userPreferences || {}));
        },
    },
});
